import { useQuery } from '@tanstack/react-query'
import React from 'react'
import * as Reakit from 'reakit/Combobox'

import { searchApiRequest } from '../../services/api'
import { link } from '../../services/routing'
import { Instrument } from '../../types'
import { Button } from '../button'
import { Centered } from '../centered'
import { COMPONENT_ANIMATION_DURATION } from '../constants'
import { Spinner } from '../spinner'
import { SearchItem } from './search-item'
import { getRedirectForTicker } from './util'

interface SearchListProps {
  state: Reakit.unstable_ComboboxStateReturn
  instrument?: Instrument
  chartIndex?: number
  isShowAllVisible: boolean
  isSearchItemLink: boolean
  onTickerSelected?: (ticker: string) => void
}

export function SearchList(props: SearchListProps) {
  const { data, isLoading } = useQuery(
    ['search', props.state.inputValue, props.instrument],
    () => searchApiRequest(props.state.inputValue, props.instrument),
    { enabled: props.state.inputValue.length > 0, keepPreviousData: true, cacheTime: Infinity, staleTime: Infinity }
  )
  const { setAnimated, hide, animated } = props.state

  React.useEffect(() => {
    if (!isLoading && !data?.length && !props.isShowAllVisible) {
      setAnimated(1)
      hide()
    } else if (animated !== COMPONENT_ANIMATION_DURATION) {
      setAnimated(COMPONENT_ANIMATION_DURATION)
    }
  }, [isLoading, data, setAnimated, hide, props.isShowAllVisible, animated])

  if (isLoading) {
    return (
      <Centered className="h-52 flex-1">
        <Spinner />
      </Centered>
    )
  }

  return (
    <div className="flex flex-1 flex-col space-y-2 overflow-hidden">
      {data && data.length !== 0 && (
        <div className="flex grow flex-col overflow-auto">
          {data.map((item) => (
            <SearchItem
              state={props.state}
              key={item.ticker}
              item={item}
              chartIndex={props.chartIndex}
              itemProps={
                props.isSearchItemLink
                  ? {
                      as: 'a',
                      href: getRedirectForTicker(item.ticker),
                    }
                  : {
                      onClick: (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                        ev.preventDefault()
                        props.onTickerSelected?.(item.ticker)
                        props.state?.setInputValue('')
                      },
                    }
              }
            />
          ))}
        </div>
      )}
      {props.isShowAllVisible && (
        <Button
          as="a"
          className="w-full shrink-0 self-center"
          contentClass="text-center"
          href={link.toSearch(encodeURIComponent(props.state.currentValue ?? props.state.inputValue))}
        >
          Show All Results
        </Button>
      )}
    </div>
  )
}
